import { useEffect, useMemo, useState } from 'react';

/**
 * Paginação client-side em cima de uma lista já carregada. Resetamos
 * para a página 1 sempre que o total muda (ex.: o usuário aplicou
 * filtro e o conjunto encolheu — não queremos ficar olhando uma página
 * que não existe mais).
 *
 * Mantém a leitura simples: a página manipula `paginados` no map; o
 * rodapé renderiza `<Paginacao />` com os mesmos valores.
 */
export function usePaginacao<T>(itens: readonly T[], tamanhoPagina = 25) {
  const [pagina, setPagina] = useState(1);
  const total = itens.length;
  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina));

  // Se o total encolheu pra menos páginas que a atual (filtro reduziu a
  // lista), volta pra última página válida em vez de mostrar vazio.
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  const paginados = useMemo(() => {
    const ini = (pagina - 1) * tamanhoPagina;
    return itens.slice(ini, ini + tamanhoPagina);
  }, [itens, pagina, tamanhoPagina]);

  return {
    pagina,
    setPagina,
    paginados,
    total,
    totalPaginas,
    tamanhoPagina,
  };
}
